import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const ConsentBanner = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    external: false,
  });

  useEffect(() => {
    const consentData = localStorage.getItem('consentPreferences');
    if (!consentData) {
      setShowBanner(true);
    } else {
      setPreferences(JSON.parse(consentData));
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      external: true,
    };
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      external: false,
    };
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem('consentPreferences', JSON.stringify(prefs));
    localStorage.setItem('consentTimestamp', new Date().toISOString());
    setShowBanner(false);
    loadScripts(prefs);
  };

  const loadScripts = (prefs) => {
    if (prefs.analytics) {
      console.log('Analytics scripts loaded');
    }
    if (prefs.marketing) {
      console.log('Marketing scripts loaded');
    }
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return;
    setPreferences({
      ...preferences,
      [key]: !preferences[key],
    });
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => setShowBanner(false)}
      ></div>

      {/* Banner */}
      <div className="relative w-full bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {!showDetails ? (
            // Main View
            <div>
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Ihre Privatsphäre ist uns wichtig
                </h2>
                <button
                  onClick={() => setShowBanner(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Schließen"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-gray-700 mb-8 max-w-2xl">
                Wir verwenden Cookies und ähnliche Technologien, um Ihre Erfahrung auf unserer Website zu verbessern, Inhalte und Werbung zu personalisieren und den Website-Verkehr zu analysieren.
              </p>

              {/* Buttons */}
              <div className="flex flex-wrap gap-4 mb-6">
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold rounded-lg transition-colors"
                >
                  Alle ablehnen
                </button>

                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Alle akzeptieren
                </button>

                <button
                  onClick={() => setShowDetails(true)}
                  className="px-6 py-3 text-gray-700 hover:text-gray-900 font-semibold border border-gray-300 rounded-lg transition-colors"
                >
                  Details anzeigen
                </button>
              </div>

              {/* Links */}
              <p className="text-sm text-gray-600">
                Weitere Informationen finden Sie in unserer{' '}
                <button
                  onClick={() => navigate('/datenschutz')}
                  className="text-green-600 hover:underline font-semibold bg-none border-none cursor-pointer"
                >
                  Datenschutzerklärung
                </button>
              </p>
            </div>
          ) : (
            // Details View
            <div>
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Cookie-Einstellungen
                </h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Schließen"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Cookie Categories */}
              <div className="space-y-4 mb-8">
                {/* Notwendig */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Notwendige Cookies</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Diese Cookies sind für die Grundfunktionen erforderlich und können nicht deaktiviert werden.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.necessary}
                      disabled
                      className="w-5 h-5 rounded mt-1 ml-4"
                    />
                  </div>
                </div>

                {/* Analytik */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Analytik-Cookies</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Helfen uns zu verstehen, wie Sie unsere Website nutzen, um sie zu verbessern.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={() => togglePreference('analytics')}
                      className="w-5 h-5 rounded mt-1 ml-4 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Marketing */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Marketing-Cookies</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Ermöglichen es uns, Werbung und Inhalte zu personalisieren.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={() => togglePreference('marketing')}
                      className="w-5 h-5 rounded mt-1 ml-4 cursor-pointer"
                    />
                  </div>
                </div>

                {/* Externe Inhalte */}
                <div className="border border-gray-300 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">Externe Inhalte</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        Videos, Maps und andere externe Inhalte von Drittanbietern.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={preferences.external}
                      onChange={() => togglePreference('external')}
                      className="w-5 h-5 rounded mt-1 ml-4 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowDetails(false)}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold rounded-lg transition-colors"
                >
                  Zurück
                </button>

                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Einstellungen speichern
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
